import { useMemo, useState } from 'react';
import { t } from 'i18next';
import { Input, Text } from '@datanomik/core-ui';
import { ArrowRightSLine, BuildingLine, SearchLine } from '@datanomik/core-ui/icons';

import { useRecentClients } from './useRecentClients';

export interface SelectableClient {
  id: string | number;
  company: string;
}

interface ClientSelectorProps {
  clients: SelectableClient[];
  busy?: boolean;
  onSelect: (client: SelectableClient) => void;
}

function ClientRow({
  client,
  busy,
  onSelect,
}: {
  client: SelectableClient;
  busy: boolean;
  onSelect: (client: SelectableClient) => void;
}) {
  return (
    <button
      type="button"
      data-testid="client-option"
      disabled={busy}
      onClick={() => onSelect(client)}
      className="flex w-full cursor-pointer items-center gap-3 rounded-lg border border-transparent px-3 py-2.5 text-left transition-colors outline-none hover:border-border hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring/40 disabled:pointer-events-none disabled:opacity-50"
    >
      <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary-subtle text-primary-subtle-foreground [&_svg]:size-4">
        <BuildingLine aria-hidden="true" />
      </span>
      <Text variant="body" className="flex-1 truncate">{client.company}</Text>
      <ArrowRightSLine aria-hidden="true" className="size-4 shrink-0 text-muted-foreground" />
    </button>
  );
}

export function ClientSelector({ clients, busy = false, onSelect }: ClientSelectorProps) {
  const [query, setQuery] = useState('');
  const { recentIds, remember } = useRecentClients();

  const term = query.trim().toLowerCase();

  const matches = useMemo(
    () => (term
      ? clients.filter((client) => client.company?.toLowerCase().includes(term))
      : clients),
    [clients, term],
  );

  const recent = useMemo(
    () => (term
      ? []
      : recentIds
        .map((id) => clients.find((client) => String(client.id) === id))
        .filter((client): client is SelectableClient => Boolean(client))),
    [clients, recentIds, term],
  );

  const rest = useMemo(
    () => matches.filter((client) => !recent.some((entry) => entry.id === client.id)),
    [matches, recent],
  );

  const choose = (client: SelectableClient) => {
    remember(String(client.id));
    onSelect(client);
  };

  return (
    <div data-modern className="flex flex-col gap-3">
      <Input
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder={t('Search')}
        aria-label={t('Search')}
        startAdornment={<SearchLine aria-hidden="true" />}
      />

      <div className="flex max-h-80 flex-col gap-3 overflow-y-auto">
        {recent.length > 0 && (
          <section className="flex flex-col gap-1">
            <Text variant="caption" tone="muted" className="px-3 uppercase tracking-wider">
              {t('Recent')}
            </Text>
            {recent.map((client) => (
              <ClientRow key={client.id} client={client} busy={busy} onSelect={choose} />
            ))}
          </section>
        )}

        {rest.length > 0 && (
          <section className="flex flex-col gap-1">
            {recent.length > 0 && (
              <Text variant="caption" tone="muted" className="px-3 uppercase tracking-wider">
                {t('All clients')}
              </Text>
            )}
            {rest.map((client) => (
              <ClientRow key={client.id} client={client} busy={busy} onSelect={choose} />
            ))}
          </section>
        )}

        {matches.length === 0 && (
          <Text variant="body-sm" tone="muted" className="py-6 text-center">
            {t('No results found')}
          </Text>
        )}
      </div>
    </div>
  );
}
